import {
  SKIN_TONES, HAIR_COLORS, EYE_COLORS, CLOAK_COLORS,
} from "@/components/Portrait";
import { classChibi } from "@/lib/classChibi";

/**
 * Isometric-friendly full-body character sprite composed with layered divs.
 * Layers (back-to-front):
 *   cloak (rear rectangle) → body → armor overlay → hair/hood → head → eyes → marking → weapon → trinket glow
 *
 * The weapon SHAPE is inferred from the item name to keep drop generation
 * completely procedural. Recognised roots: sword, blade, dagger, axe, mace,
 * staff, bow, book, shield.
 *
 * Props:
 *   size       — base pixel width. Height is 1.6× size (portrait 3:5).
 *   appearance — {skin, hair, hair_color, eyes, cloak, marking}
 *   equipped   — {weapon:{name,rarity}, armor:{...}, trinket:{...}}
 *   cls        — class for accent tint if appearance missing
 *   cloak_color— optional cosmetic override
 *   sitting    — bool: renders a seated pose
 *   facing     — 'front' | 'left' | 'right' (default 'front')
 */
export default function FullBodyCharacter({
  size = 64,
  appearance,
  equipped,
  cls = "Warrior",
  cloak_color,
  sitting = false,
  idle = false,
  facing = "front",
  data_testid,
}) {
  const skin = SKIN_TONES[appearance?.skin] || "#B08A5E";
  const hairColor = HAIR_COLORS[appearance?.hair_color] || "#1a1a1a";
  const eyeColor = EYE_COLORS[appearance?.eyes] || "#E55A00";
  const hair = appearance?.hair || "short";
  const marking = appearance?.marking || "none";
  const cloak = cloak_color
    || CLOAK_COLORS[appearance?.cloak]
    || (cls === "Warrior" ? "#5A0A0A" : cls === "Guardian" ? "#1A2333" : cls === "Rogue" ? "#2B2B2B" : cls === "Wizard" ? "#3A1F5E" : "#2F3D1E");

  const w = size;
  const h = Math.round(size * 1.6);

  // ─── Chibi full illustration (if class+gender known) ──────────────
  // Preferred rendering path: replaces every procedural layer with the
  // official chibi art. Weapon / armor / shield indicators are drawn as
  // small badges beneath the sprite so equipment is still readable.
  const chibi = classChibi(cls, appearance?.gender, appearance?.class_image);
  if (chibi) {
    const RARITY_GLOW = { common: "#B0ADA5", uncommon: "#6E8B3D", rare: "#4A7BC7", epic: "#8A2BE2", legendary: "#E5C34A", mythic: "#E33333" };
    const weaponTint = equipped?.weapon ? (RARITY_GLOW[equipped.weapon.rarity] || "#B0ADA5") : null;
    const armorTint = equipped?.armor ? (RARITY_GLOW[equipped.armor.rarity] || "#8c8c8c") : null;
    const trinketTint = equipped?.trinket ? (RARITY_GLOW[equipped.trinket.rarity] || "#B89C3A") : null;
    const sitScale = sitting ? 0.85 : 1;
    // Idle breathing offset — deterministic per-character so a hall of players
    // desynchronises rather than pulsing in lockstep. Uses appearance seed.
    const seed = `${cls}${appearance?.gender || ""}${appearance?.skin || ""}${appearance?.hair || ""}`;
    const seedNum = seed.split("").reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 5381);
    const delayS = (seedNum % 1000) / 1000; // 0-1s
    const durationS = 2.8 + ((seedNum >> 8) % 8) * 0.15; // 2.8-3.9s
    return (
      <div
        className="relative shrink-0"
        style={{ width: w, height: h }}
        data-testid={data_testid || "fullbody"}
        data-sitting={sitting ? "1" : "0"}
      >
        <img
          src={chibi}
          alt={`${cls} chibi`}
          draggable={false}
          className="pointer-events-none select-none"
          style={{
            width: w,
            height: h,
            objectFit: "contain",
            objectPosition: "50% 100%",
            filter: sitting ? "brightness(0.95)" : "none",
            transform: `scale(${sitScale}) translateY(${sitting ? "6%" : "0"})`,
            transformOrigin: "center bottom",
            imageRendering: "auto",
            animation: sitting
              ? undefined
              : idle
                ? `chibi-idle-emote ${(durationS * 1.6).toFixed(2)}s ease-in-out ${delayS}s infinite`
                : `chibi-breath ${durationS}s ease-in-out ${delayS}s infinite`,
          }}
        />
        {/* Cloak tint aura (subtle, respects palette without covering art) */}
        {(cloak_color || appearance?.cloak) && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(60% 40% at 50% 90%, ${cloak}44, transparent 70%)`,
            }}
          />
        )}
        {/* Equipment glow badges — placed at feet so they don't cover the face */}
        {(weaponTint || armorTint || trinketTint) && (
          <div
            className="absolute left-0 right-0 flex justify-center gap-1"
            style={{ bottom: -2 }}
          >
            {weaponTint && (
              <span
                data-testid="equip-weapon-badge"
                title={equipped.weapon.name}
                style={{
                  width: 6, height: 6,
                  background: weaponTint,
                  boxShadow: `0 0 4px ${weaponTint}`,
                  border: "1px solid #050505",
                }}
              />
            )}
            {armorTint && (
              <span
                data-testid={`equip-armor-badge`}
                title={equipped.armor.name}
                style={{
                  width: 6, height: 6,
                  background: armorTint,
                  boxShadow: `0 0 4px ${armorTint}`,
                  border: "1px solid #050505",
                }}
              />
            )}
            {trinketTint && (
              <span
                data-testid="equip-trinket-badge"
                title={equipped.trinket.name}
                style={{
                  width: 6, height: 6,
                  background: trinketTint,
                  boxShadow: `0 0 4px ${trinketTint}`,
                  border: "1px solid #050505",
                  borderRadius: "50%",
                }}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  // ─── Legacy CSS-layered sprite (fallback when chibi is unavailable) ─
  const bodyW = Math.round(w * 0.5);
  const bodyH = Math.round(h * 0.35);
  const headSize = Math.round(w * 0.5);
  const legH = sitting ? Math.round(h * 0.14) : Math.round(h * 0.28);
  const legW = Math.round(w * 0.15);

  const weaponName = (equipped?.weapon?.name || "").toLowerCase();
  const armorItem = equipped?.armor;
  const trinket = equipped?.trinket;

  const RARITY_GLOW = { common: "#B0ADA5", uncommon: "#6E8B3D", rare: "#4A7BC7", epic: "#8A2BE2", legendary: "#E5C34A", mythic: "#E33333" };
  const armorTint = armorItem ? (RARITY_GLOW[armorItem.rarity] || "#8c8c8c") : null;
  const weaponTint = equipped?.weapon ? (RARITY_GLOW[equipped.weapon.rarity] || "#B0ADA5") : "#B0ADA5";

  // ─── Weapon shape (right hand) ────────────────────────────────────
  const weaponEl = (() => {
    if (!equipped?.weapon) return null;
    const style = {
      position: "absolute",
      right: -Math.round(w * 0.05),
      top: sitting ? Math.round(h * 0.44) : Math.round(h * 0.38),
      pointerEvents: "none",
    };
    if (/bow/.test(weaponName)) {
      return (
        <div style={style} data-testid="weapon-bow">
          <div style={{ width: 3, height: Math.round(h * 0.32), background: weaponTint, borderRadius: 2, boxShadow: `0 0 3px ${weaponTint}` }} />
          <div style={{ position: "absolute", top: 0, left: 0, width: 1, height: Math.round(h * 0.32), background: "#E2DFD8", opacity: 0.7 }} />
        </div>
      );
    }
    if (/staff|book|sermon/.test(weaponName)) {
      const isBook = /book|sermon/.test(weaponName);
      return isBook ? (
        <div style={{ ...style, width: Math.round(w * 0.16), height: Math.round(w * 0.20), background: weaponTint, border: "1px solid #050505" }} data-testid="weapon-book" />
      ) : (
        <div style={style} data-testid="weapon-staff">
          <div style={{ width: 3, height: Math.round(h * 0.40), background: weaponTint }} />
          <div style={{ position: "absolute", top: -Math.round(w * 0.06), left: -3, width: Math.round(w * 0.18), height: Math.round(w * 0.18), borderRadius: "50%", background: `radial-gradient(${weaponTint}, transparent)`, boxShadow: `0 0 6px ${weaponTint}` }} />
        </div>
      );
    }
    if (/axe/.test(weaponName)) {
      return (
        <div style={style} data-testid="weapon-axe">
          <div style={{ width: 3, height: Math.round(h * 0.30), background: "#6B4A2A" }} />
          <div style={{ position: "absolute", top: -2, left: -6, width: Math.round(w * 0.24), height: Math.round(w * 0.16), background: weaponTint, clipPath: "polygon(0 0, 100% 20%, 100% 80%, 0 100%)", border: "1px solid #050505" }} />
        </div>
      );
    }
    if (/mace/.test(weaponName)) {
      return (
        <div style={style} data-testid="weapon-mace">
          <div style={{ width: 3, height: Math.round(h * 0.28), background: "#6B4A2A" }} />
          <div style={{ position: "absolute", top: -Math.round(w * 0.06), left: -Math.round(w * 0.06), width: Math.round(w * 0.20), height: Math.round(w * 0.20), borderRadius: "20%", background: weaponTint, boxShadow: `0 0 4px ${weaponTint}` }} />
        </div>
      );
    }
    if (/dagger|whisper/.test(weaponName)) {
      return (
        <div style={style} data-testid="weapon-dagger">
          <div style={{ width: 2, height: Math.round(h * 0.18), background: weaponTint, boxShadow: `0 0 3px ${weaponTint}` }} />
          <div style={{ position: "absolute", top: Math.round(h * 0.18), left: -2, width: Math.round(w * 0.12), height: 2, background: "#B89C3A" }} />
        </div>
      );
    }
    // sword / blade / default
    return (
      <div style={style} data-testid="weapon-sword">
        <div style={{ width: 3, height: Math.round(h * 0.30), background: weaponTint, boxShadow: `0 0 3px ${weaponTint}` }} />
        <div style={{ position: "absolute", top: Math.round(h * 0.30), left: -3, width: Math.round(w * 0.16), height: 2, background: "#B89C3A" }} />
      </div>
    );
  })();

  // ─── Sitting-pose adjustments ─────────────────────────────────────
  const sitOffsetY = sitting ? Math.round(h * 0.08) : 0;

  return (
    <div
      className="relative shrink-0"
      style={{ width: w, height: h }}
      data-testid={data_testid || "fullbody"}
      data-sitting={sitting ? "1" : "0"}
    >
      {/* Cloak — behind body */}
      <div
        aria-hidden
        className="absolute"
        style={{
          left: Math.round(w * 0.15),
          top: Math.round(h * 0.20) + sitOffsetY,
          width: Math.round(w * 0.70),
          height: Math.round(h * (sitting ? 0.48 : 0.62)),
          background: cloak,
          boxShadow: "inset 0 4px 0 rgba(0,0,0,0.35)",
          clipPath: sitting
            ? "polygon(10% 0, 90% 0, 100% 80%, 0 80%)"
            : "polygon(15% 0, 85% 0, 100% 100%, 0 100%)",
        }}
      />

      {/* Body torso */}
      <div
        className="absolute"
        style={{
          left: Math.round(w / 2 - bodyW / 2),
          top: Math.round(h * 0.28) + sitOffsetY,
          width: bodyW,
          height: bodyH,
          background: skin,
          boxShadow: "inset 0 -6px 0 rgba(0,0,0,0.25)",
        }}
      />

      {/* Armor overlay */}
      {armorItem && (
        <div
          className="absolute"
          data-testid={`armor-${armorItem.slot}`}
          title={armorItem.name}
          style={{
            left: Math.round(w / 2 - bodyW / 2) - 2,
            top: Math.round(h * 0.29) + sitOffsetY,
            width: bodyW + 4,
            height: Math.round(bodyH * 0.9),
            background: `linear-gradient(180deg, ${armorTint}dd, ${armorTint}77)`,
            border: `1px solid ${armorTint}`,
            boxShadow: `inset 0 -6px 0 rgba(0,0,0,0.35), 0 0 4px ${armorTint}44`,
          }}
        />
      )}

      {/* Arms */}
      <div
        className="absolute"
        style={{
          left: Math.round(w / 2 - bodyW / 2) - Math.round(w * 0.08),
          top: Math.round(h * 0.29) + sitOffsetY,
          width: Math.round(w * 0.08),
          height: Math.round(bodyH * 0.85),
          background: skin,
        }}
      />
      <div
        className="absolute"
        style={{
          right: Math.round(w / 2 - bodyW / 2) - Math.round(w * 0.08),
          top: Math.round(h * 0.29) + sitOffsetY,
          width: Math.round(w * 0.08),
          height: Math.round(bodyH * 0.85),
          background: skin,
        }}
      />

      {/* Legs */}
      <div
        className="absolute"
        style={{
          left: Math.round(w * 0.30),
          top: Math.round(h * 0.62) + sitOffsetY,
          width: legW,
          height: legH,
          background: sitting ? cloak : "#1a1a1a",
          transform: sitting ? "rotate(20deg)" : "none",
          transformOrigin: "top left",
        }}
      />
      <div
        className="absolute"
        style={{
          right: Math.round(w * 0.30),
          top: Math.round(h * 0.62) + sitOffsetY,
          width: legW,
          height: legH,
          background: sitting ? cloak : "#1a1a1a",
          transform: sitting ? "rotate(-20deg)" : "none",
          transformOrigin: "top right",
        }}
      />

      {/* Head */}
      <div
        className="absolute"
        style={{
          left: Math.round(w / 2 - headSize / 2),
          top: Math.round(h * 0.08) + sitOffsetY,
          width: headSize,
          height: headSize,
          background: skin,
          boxShadow: "inset 0 -4px 0 rgba(0,0,0,0.25)",
        }}
      />

      {/* Hair / hood */}
      {hair === "hood" && (
        <div
          className="absolute"
          style={{
            left: Math.round(w / 2 - headSize / 2) - 3,
            top: Math.round(h * 0.05) + sitOffsetY,
            width: headSize + 6,
            height: headSize + 4,
            background: cloak,
            filter: "brightness(0.7)",
            clipPath: "polygon(0 100%, 12% 30%, 50% 0, 88% 30%, 100% 100%)",
          }}
        />
      )}
      {hair !== "hood" && hair !== "bald" && (
        <>
          <div
            className="absolute"
            style={{
              left: Math.round(w / 2 - headSize / 2),
              top: Math.round(h * 0.05) + sitOffsetY,
              width: headSize,
              height: hair === "mohawk" ? Math.round(headSize * 0.45) : Math.round(headSize * 0.28),
              background: hairColor,
              clipPath: hair === "mohawk" ? "polygon(35% 0, 65% 0, 65% 100%, 35% 100%)" : "none",
            }}
          />
          {hair === "long" && (
            <>
              <div
                className="absolute"
                style={{
                  left: Math.round(w / 2 - headSize / 2) - 2,
                  top: Math.round(h * 0.12) + sitOffsetY,
                  width: 5,
                  height: Math.round(headSize * 0.9),
                  background: hairColor,
                }}
              />
              <div
                className="absolute"
                style={{
                  right: Math.round(w / 2 - headSize / 2) - 2,
                  top: Math.round(h * 0.12) + sitOffsetY,
                  width: 5,
                  height: Math.round(headSize * 0.9),
                  background: hairColor,
                }}
              />
            </>
          )}
        </>
      )}

      {/* Eyes */}
      <div
        className="absolute"
        style={{
          left: Math.round(w / 2 - headSize / 2) + Math.round(headSize * 0.20),
          top: Math.round(h * 0.20) + sitOffsetY,
          width: 3, height: 3,
          background: eyeColor,
          boxShadow: `0 0 4px ${eyeColor}`,
        }}
      />
      <div
        className="absolute"
        style={{
          left: Math.round(w / 2 - headSize / 2) + Math.round(headSize * 0.65),
          top: Math.round(h * 0.20) + sitOffsetY,
          width: 3, height: 3,
          background: eyeColor,
          boxShadow: `0 0 4px ${eyeColor}`,
        }}
      />

      {/* Marking */}
      {marking === "scar" && (
        <div
          className="absolute"
          style={{
            left: Math.round(w / 2) + Math.round(headSize * 0.15),
            top: Math.round(h * 0.16) + sitOffsetY,
            width: 2, height: Math.round(headSize * 0.4),
            background: "#8A0303", transform: "rotate(15deg)", opacity: 0.85,
          }}
        />
      )}
      {marking === "warpaint" && (
        <div
          className="absolute"
          style={{
            left: Math.round(w / 2 - headSize / 2) + Math.round(headSize * 0.15),
            top: Math.round(h * 0.25) + sitOffsetY,
            width: Math.round(headSize * 0.7), height: 2,
            background: "#8A0303", opacity: 0.9,
          }}
        />
      )}
      {marking === "brand" && (
        <div
          className="absolute"
          style={{
            left: Math.round(w / 2 - 3),
            top: Math.round(h * 0.18) + sitOffsetY,
            width: 6, height: 6,
            borderRadius: "50%",
            border: "1px solid #E55A00",
            boxShadow: "0 0 3px #E55A00",
          }}
        />
      )}

      {/* Weapon */}
      {weaponEl}

      {/* Shield (left hand if armor.name contains 'shield') */}
      {armorItem && /shield/i.test(armorItem.name) && (
        <div
          className="absolute"
          data-testid="shield-visible"
          style={{
            left: -Math.round(w * 0.05),
            top: Math.round(h * 0.36) + sitOffsetY,
            width: Math.round(w * 0.20),
            height: Math.round(h * 0.24),
            background: armorTint,
            border: "1px solid #050505",
            clipPath: "polygon(50% 0, 100% 30%, 100% 70%, 50% 100%, 0 70%, 0 30%)",
            boxShadow: `0 0 4px ${armorTint}77`,
          }}
        />
      )}

      {/* Trinket glow (subtle aura around head) */}
      {trinket && (
        <div
          aria-hidden
          className="absolute"
          style={{
            left: Math.round(w / 2 - headSize / 2) - 4,
            top: Math.round(h * 0.06) + sitOffsetY - 4,
            width: headSize + 8, height: headSize + 8,
            border: `1px solid ${RARITY_GLOW[trinket.rarity] || "#B89C3A"}`,
            boxShadow: `0 0 8px ${RARITY_GLOW[trinket.rarity] || "#B89C3A"}55`,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
